import { mapActions, mapGetters, mapState } from 'vuex';
import marketContractHorizontalSushiCommonBetslip from '@/components/market/market-contract-horizontal-sushi-common-betslip/market-contract-horizontal-sushi-common-betslip.vue';
import marketContractsListHeaderSushiCommonBetslip from '@/components/market/market-contracts-list-header-sushi-common-betslip/market-contracts-list-header-sushi-common-betslip.vue';
import purchaseInlineSushi from '@/components/purchase/sushi/purchase-inline-sushi/purchase-inline-sushi.vue';
import relatedContractsSushi from '@/components/related-contracts-sushi/related-contracts-sushi.vue';
import Contract from '@/models/contract';
import sushiMarketTopComments
  from '@/components/pages/page-market-detail-sushi/pages/sushi-market-contracts/components/sushi-market-top-comments/sushi-market-top-comments.vue';
import headerHeightMixin from '@/mixins/headerHeight';
import { isMarketSingle, isMarketDisabled, contractOwnershipNum } from '@/utils/helpers';
import { sumBy, debounce, isEqual } from 'lodash';
import scrollTo from '@/utils/scrollTo';
import log from '@/logger';
import {
  BUTTONS,
  VALUES,
} from '@/components/market/market-contract-horizontal-sushi/market-contract-horizontal-sushi'
import ls from '@/helpers/localStorage'
import { APP_MOBILE_LAYOUT } from '@/store/modules/app';

export default {
  mixins: [
    headerHeightMixin,
  ],
  components: {
    marketContractHorizontalSushi: marketContractHorizontalSushiCommonBetslip,
    marketContractsListHeaderSushi: marketContractsListHeaderSushiCommonBetslip,
    purchaseInlineSushi,
    relatedContractsSushi,
    sushiMarketTopComments,
  },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    isPageTitleSticky: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      oddsReinit: false,
      oddsTimeout: null,
      shownOnInit: false,
      showAllContracts: false,
    };
  },
  created() {
    this.debouncedPurchaseJourneyOpened = debounce(this.purchaseJourneyOpened, 300);
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop']),
    ...mapState('app', ['settingMobileLayout']),
    ...mapState('market', ['market', 'contracts']),
    ...mapState('trade', ['tradeOptions', 'hidePurchaseForContractId']),
    ...mapState('user', ['status']),
    ...mapGetters('user', ['isLoggedIn']),
    isStandardMobileLayout() {
      return this.isLayoutMobile && this.settingMobileLayout === APP_MOBILE_LAYOUT.STANDARD;
    },
    isSimplifiedMobileLayout() {
      return this.isLayoutMobile && this.settingMobileLayout === APP_MOBILE_LAYOUT.SIMPLIFIED;
    },
    isCondensedMobileLayout() {
      return this.isLayoutMobile && this.settingMobileLayout === APP_MOBILE_LAYOUT.CONDENSED;
    },
    marketDetailMobileLayout() {
      return this.isLayoutMobile ? this.settingMobileLayout : null;
    },
    isSingleContractMarket() {
      return this.isMarketSingle && this.contracts.length === 1;
    },
    singleContractId() {
      return this.isSingleContractMarket ? this.contracts[0].contractId : null;
    },
    showRelatedContractsMobile() {
      return this.isLayoutMobile && !this.isMarketDisabled && this.singleContractId !== null;
    },
    relatedContractsMobileContractId() {
      return this.showRelatedContractsMobile ? this.singleContractId : null;
    },
    betslipStyleModifiers() {
      const $toolbar = document.querySelector('.page-market-detail-sushi__toolbar');
      const $stickyTitle = document.querySelector('.page-market-detail-sushi__title--is-sticky');
      const toolbarHeight = $toolbar ? $toolbar.offsetHeight : 43;
      const stickyTitleHeight = this.isPageTitleSticky && $stickyTitle ? $stickyTitle.offsetHeight : 0;

      return {
        // Keep the old desktop breathing room above the order slip, then layer in
        // the sticky short-title height when that extra pinned row is active.
        top: `${this.headerHeight + 20 + stickyTitleHeight + toolbarHeight}px`,
      };
    },
    visibleContracts() {
      return this.showAllContracts ? this.contracts : this.notHiddenContracts;
    },
    notHiddenContracts() {
      return this.contracts.filter(i => !i.hiddenByDefault);
    },
    hiddenContracts() {
      return this.contracts.filter(i => i.hiddenByDefault);
    },
    shouldShowToggleMoreButton() {
      return this.hiddenContracts.length > 0;
    },
    shouldInitializePurchaseOnInit() {
      const isTradeOptionsValid = this.tradeOptions && this.tradeOptions.contractId;
      const isContractInMarket = isTradeOptionsValid && this.contracts.some(c => c.contractId === this.tradeOptions.contractId);

      if (isTradeOptionsValid && isContractInMarket) {
        return false;
      }

      return this.contracts.length > 0 && !this.isMarketDisabled && !this.shownOnInit;
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    isMarketDisabled() {
      return isMarketDisabled(this.market);
    },
    hiddenContractsOffersOrSharesCount() {
      if (this.hiddenContracts.length > 0) {
        return sumBy(this.hiddenContracts, contractOwnershipNum);
      }
      return 0;
    },
    toggleMoreModifiers() {
      return {
        'sushi-market-contracts__toggle': true,
        'sushi-market-contracts__toggle--offers-or-shares': this.hiddenContractsOffersOrSharesCount > 0,
        'sushi-market-contracts__toggle--active': this.showAllContracts,
      };
    },
    routeContractId() {
      return parseInt(this.$route.query.contractId || '0', 10);
    },
    routeContractForceNoSide() {
      return (this.$route.query.no || '').toLowerCase() === 'true';
    },
    tradeOptionsAndHidePurchaseForContractId() {
      return {
        tradeOptions: this.tradeOptions,
        hidePurchaseForContractId: this.hidePurchaseForContractId,
        contractId: this.tradeOptions.contractId,
        wasActive: this.showInlinePurchase(this.tradeOptions.contractId),
      };
    },
  },
  methods: {
    ...mapActions('trade', ['setTradeOptions', 'setTradePrice', 'toggleHidePurchaseForContractId', 'setBottomWidgetFooterMode', 'setBottomBetSlipExtendedMode']),
    ...mapActions('market', ['pushSessionHistoryContract']),
    resetContractsViewForMarketChange() {
      this.shownOnInit = false;
      this.showAllContracts = false;
    },
    shouldInitializePurchaseOnInitHandler(value) {
      if (value && this.visibleContracts.length > 0 && !this.shownOnInit) {
        this.shownOnInit = true;
        setTimeout(() => {
          if (this.routeContractId) {
            const inContracts = this.contracts.map(i => i.contractId).includes(this.routeContractId);
            const inHiddenContracts = this.hiddenContracts.map(i => i.contractId).includes(this.routeContractId);

            if (inContracts) {
              if (inHiddenContracts) {
                this.showAllContracts = true;
              }
              this.$nextTick(() => {
                this.openContract({ contractId: this.routeContractId, forceNoSide: this.routeContractForceNoSide });
              });
              return;
            }
          }
          this.openFirstContract();
        }, 250);
      }
    },
    hidePurchaseForDefaultPick(contractId) {
      if (!this.isLayoutMobile) return;

      this.$nextTick(() => {
        log.info(`[PURCHASE] hiding for ${contractId}`);
        this.toggleHidePurchaseForContractId(contractId);
      });
    },
    getDefaultButtonForContract(contract, forceNoSide = false) {
      if (forceNoSide) {
        return BUTTONS.BUY_NO;
      }

      const contractModel = new Contract(contract);
      return contractModel.mode === VALUES.NO ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
    },
    selectDefaultContract({ contractId, forceNoSide = false }) {
      const contract = this.contracts.find(c => c.contractId === contractId);
      if (!contract) {
        log.info('[PURCHASE] no contract to open');
        return;
      }

      const button = this.getDefaultButtonForContract(contract, forceNoSide);
      log.info('[PURCHASE] selecting default contract', contractId);

      this.setTradeOptions({
        contractId: contract.contractId,
        operationType: button.type,
        operationValue: button.value,
      });
      this.pushSessionHistoryContract({
        value: {
          ...contract,
          marketName: this.market.marketShortName || this.market.marketName
        }
      });
      this.hidePurchaseForDefaultPick(contract.contractId);
      if (this.isLayoutMobile) {
        this.setBottomBetSlipExtendedMode(false);
      }
    },
    openContract({ contractId, forceNoSide = false }) {
      setTimeout(() => {
        this.selectDefaultContract({ contractId, forceNoSide });
      }, 250);
    },
    openFirstContract() {
      if (this.visibleContracts.length > 0) {
        const contractId = this.visibleContracts[0].contractId;
        this.selectDefaultContract({ contractId });
      }
    },
    purchaseJourneyOpened(contractId) {
      setTimeout(() => {
        log.info('[PURCHASE] journey opened', contractId);
        const $el = document.querySelector(`[data-contract-id="${contractId}"]`);
        if (!$el) {
          log.info('[PURCHASE] journey opened cannot find contract');
          return;
        }
        let topOffset = this.headerHeight;

        if (this.isLayoutDesktop) {
          const $toolbar = document.querySelector('.page-market-detail-sushi__toolbar');
          if ($toolbar) {
            topOffset += $toolbar.offsetHeight;
          }
        } else {
          const $title = document.querySelector('.page-market-detail-sushi__title');
          if ($title) {
            topOffset += $title.offsetHeight;
          }
        }

        // Disabled atm
        if (!this.oddsReinit && this.isLayoutMobile && false) {
          scrollTo($el, 250, 'linear', null, topOffset);
        }
      }, 250);
    },
    onToggleMore() {
      this.showAllContracts = !this.showAllContracts;
    },
    onOpenDiscussion() {
      this.$emit('open-discussion');
    },
    onOpenComment(comment) {
      this.$emit('open-comment', comment);
    },
    showInlinePurchase(contractId) {
      if (this.isCondensedMobileLayout) {
        return false;
      }
      if (this.tradeOptions && this.tradeOptions.contractId === contractId && this.hidePurchaseForContractId === contractId) {
        return false;
      }
      return this.tradeOptions && this.tradeOptions.contractId === contractId;
    },
  },
  beforeDestroy() {
    if (this.debouncedPurchaseJourneyOpened) {
      this.debouncedPurchaseJourneyOpened.cancel();
    }
    if (this.oddsTimeout) {
      clearTimeout(this.oddsTimeout);
    }
  },
  watch: {
    'tradeOptions.contractId': {
      handler(newId, oldId) {
        // clear show all for order book
        if (newId !== oldId && oldId) {
          ls.del(`order_book_prices_show_all_${oldId}`);
          ls.del(`order_book_shares_show_all_${oldId}`);
          ls.del(`order_book_all_offers_show_all_${oldId}`);
        }
      },
    },
    'market.marketId'(value, oldValue) {
      if (!value || !oldValue || value === oldValue) {
        return;
      }

      this.resetContractsViewForMarketChange();
    },
    tradeOptionsAndHidePurchaseForContractId(newVal, oldVal) {
      // TODO: possible rmove for improve when switching tradeOptions and initializing scroll
      const newTradeOptions = newVal.tradeOptions;
      const newUpdateMarker = {
        tradeOptions: newVal.tradeOptions,
        hidePurchaseForContractId: newVal.hidePurchaseForContractId,
      };
      const oldUpdateMarker = {
        tradeOptions: oldVal.tradeOptions,
        hidePurchaseForContractId: oldVal.hidePurchaseForContractId,
      };

      const isNonEmpty = newTradeOptions && newTradeOptions.contractId;
      const updateTrigger = !isEqual(newUpdateMarker, oldUpdateMarker);
      const activeTrigger = newVal.wasActive !== oldVal.wasActive || newVal.contractId !== oldVal.contractId;
      const shouldDisplayJourney = newTradeOptions && (newTradeOptions.contractId || this.isMarketSingle) && this.showInlinePurchase(newTradeOptions.contractId)
      const shouldTriggerOpen = isNonEmpty && updateTrigger && activeTrigger && shouldDisplayJourney;

      if (shouldTriggerOpen) {
        this.debouncedPurchaseJourneyOpened(newTradeOptions.contractId);
      }
    },
    shouldInitializePurchaseOnInit: {
      handler: 'shouldInitializePurchaseOnInitHandler',
      immediate: true,
    },
  },
};




// WEBPACK FOOTER //
// ./src/components/pages/page-market-detail-sushi/pages/sushi-market-contracts/sushi-market-contracts.js